import { Box } from "@rebass/grid";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  DisplayText,
  TextStyle,
  Icon,
  Stack,
} from "@rentiohq/web-shared/dist/components";
import { useNavigate } from "react-router-dom";
import { EActionStatus, IChecklistItem } from "../ChecklistItem";
import { ProgressBar } from "../ProgressBar";
import * as S from "./AdministrationItems.styled";
import { IAdministrationItem } from "./AdministrationItems.types";

interface IProps {
  items: IAdministrationItem[];
  showCircles?: boolean;
  forExitChecklist?: boolean;
  checklistItems: IChecklistItem[];
  showChecklistItems: boolean;
  setShowChecklistItems: (show: boolean) => void;
}

const EntryItem = ({
  id,
  label,
  icon,
  tooltip,
  link,
  status,
  onClick,
}: IAdministrationItem) => {
  const navigate = useNavigate();
  const handleClick = () => {
    if (link) {
      navigate(link);
      return;
    }

    onClick?.();
  };

  const renderTooltip = () => {
    const tooltipTitle = getLocalizedText(
      `contract.administration_entry_item.${id}.tooltip.label`,
    );

    return (
      <div>
        <b>{tooltipTitle}</b>
        <div>{tooltip}</div>
      </div>
    );
  };

  const renderItemCircle = () => {
    if (!tooltip) {
      return (
        <S.Circle status={status} onClick={handleClick}>
          <div>
            <Icon size="medium" source={icon} />
          </div>
        </S.Circle>
      );
    }

    return (
      <S.Tooltip
        status={status}
        tooltipContent={renderTooltip()}
        placement="right"
      >
        <S.Circle status={status} onClick={handleClick}>
          <div>
            <Icon size="medium" source={icon} />
          </div>
        </S.Circle>
      </S.Tooltip>
    );
  };

  return (
    <Stack vertical={true} alignment="center" spacing="tight">
      <S.Label>{label}</S.Label>
      {renderItemCircle()}
    </Stack>
  );
};

export const AdministrationItems = ({
  items,
  showCircles,
  forExitChecklist,
  checklistItems,
  showChecklistItems,
  setShowChecklistItems,
}: IProps) => {
  let checklistTotal = 0;
  let checklistPending = 0;
  let checklistCompleted = 0;

  checklistItems.forEach(message => {
    if (!message) {
      return;
    }

    checklistTotal += 1;

    switch (message.actionStatus) {
      case EActionStatus.PendingRentio:
        checklistPending += 1;
        break;

      case EActionStatus.CompletedInRentio:
      case EActionStatus.CompletedManually:
        checklistCompleted += 1;
        break;

      default:
        break;
    }
  });

  return (
    <Box>
      <S.Header>
        <DisplayText size="extraSmall">
          {getLocalizedText(
            `property.dashboard.rental_administration_${
              forExitChecklist ? "exit" : "entry"
            }.heading`,
          )}
        </DisplayText>
        {(showChecklistItems || showCircles) && (
          <S.ChecklistProgressWrap>
            <ProgressBar
              completed={checklistCompleted}
              pending={checklistPending}
              total={checklistTotal}
            />

            <TextStyle variation="subdued" style={{ marginBottom: "8px" }}>
              {checklistCompleted}/{checklistTotal}
            </TextStyle>
          </S.ChecklistProgressWrap>
        )}
      </S.Header>

      {(showChecklistItems || showCircles) && (
        <S.EntryItemsWrap>
          {items.map(item => (
            <EntryItem {...item} />
          ))}
        </S.EntryItemsWrap>
      )}

      <Button
        appearance="link"
        onClick={() => setShowChecklistItems(!showChecklistItems)}
      >
        {getLocalizedText(
          showChecklistItems
            ? "contracts.detail.hide_checklist.action"
            : "contracts.detail.show_checklist.action",
        )}
      </Button>
    </Box>
  );
};
